import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Target, 
  Eye, 
  Heart,
  Shield,
  Lightbulb,
  Users,
  Award,
  Building2,
  MapPin,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_holder-enterprise/artifacts/dtw57gqk_holdergroup.png';
const HAS_LOGO_URL = 'https://customer-assets.emergentagent.com/job_holder-enterprise/artifacts/ea0sayna_1.5_Aplica%C3%A7%C3%A3o-de-Logotipo_Fundo-Escuro.jpg';

const Sobre = () => {
  const { t } = useLanguage();

  const values = [
    { icon: <Award className="h-6 w-6" />, titleKey: 'about.excellence', descKey: 'about.excellenceDesc' },
    { icon: <Lightbulb className="h-6 w-6" />, titleKey: 'about.innovation', descKey: 'about.innovationDesc' },
    { icon: <Shield className="h-6 w-6" />, titleKey: 'about.security', descKey: 'about.securityDesc' },
    { icon: <Heart className="h-6 w-6" />, titleKey: 'about.compliance', descKey: 'about.complianceDesc' },
    { icon: <Users className="h-6 w-6" />, titleKey: 'about.clientFocus', descKey: 'about.clientFocusDesc' },
  ];

  const team = [
    {
      nameKey: 'about.team.erico.name',
      roleKey: 'about.team.erico.role',
      descKey: 'about.team.erico.desc'
    },
    {
      nameKey: 'about.team.nelson.name',
      roleKey: 'about.team.nelson.role',
      descKey: 'about.team.nelson.desc'
    },
    {
      nameKey: 'about.team.renan.name',
      roleKey: 'about.team.renan.role',
      descKey: 'about.team.renan.desc'
    },
    {
      nameKey: 'about.team.paulo.name',
      roleKey: 'about.team.paulo.role',
      descKey: 'about.team.paulo.desc'
    },
    {
      nameKey: 'about.team.kamila.name',
      roleKey: 'about.team.kamila.role',
      descKey: 'about.team.kamila.desc'
    },
    {
      nameKey: 'about.team.tyler.name',
      roleKey: 'about.team.tyler.role',
      descKey: 'about.team.tyler.desc'
    },
  ];

  const offices = [
    {
      city: 'São Paulo',
      isMatriz: true,
      address: 'Avenida Moema, 87 - Bl A, Sala 152 - Moema',
      cep: '04077-020',
      image: 'https://customer-assets.emergentagent.com/job_holder-enterprise/artifacts/sjlck9hv_S%C3%83O%20PAULO.png'
    },
    {
      city: 'Porto Alegre',
      isMatriz: false,
      address: 'R. Dom Pedro II, 1240 - São João sala 406',
      cep: '90550-141',
      image: 'https://customer-assets.emergentagent.com/job_holder-enterprise/artifacts/smj14082_PORTO%20ALEGRE.png'
    },
    {
      city: 'Curitiba',
      isMatriz: false,
      address: 'Av. Iguacu, 2820 - Batel',
      cep: '80240-030',
      image: 'https://customer-assets.emergentagent.com/job_holder-enterprise/artifacts/6b0d563y_CURITIBA.png'
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="sobre-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                {t('about.historyTag')}
              </span>
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-stone-900 mb-6">
                {t('about.historyTitle')}{' '}
                <span className="gold-sheen">2005</span>
              </h1>
              <p className="text-lg text-stone-600 leading-relaxed mb-6">
                {t('about.historyDesc1')}
              </p>
              <p className="text-stone-600 leading-relaxed">
                {t('about.historyDesc2')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-emerald/10 rounded-sm blur-2xl" />
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                alt="Holder & Co. Team"
                className="relative rounded-sm shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission, Vision */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-emerald p-10 rounded-sm text-white"
              data-testid="mission-card"
            >
              <Target className="h-12 w-12 mb-6 text-bronze" />
              <h2 className="text-2xl font-serif font-bold mb-4">{t('about.missionTitle')}</h2>
              <p className="text-stone-300 leading-relaxed">
                {t('about.missionDesc')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-stone-100 p-10 rounded-sm"
              data-testid="vision-card"
            >
              <Eye className="h-12 w-12 mb-6 text-emerald" />
              <h2 className="text-2xl font-serif font-bold text-stone-900 mb-4">{t('about.visionTitle')}</h2>
              <p className="text-stone-600 leading-relaxed">
                {t('about.visionDesc')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block"
            >
              {t('about.valuesTag')}
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-serif font-bold text-stone-900"
            >
              {t('about.valuesTitle')}
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-sm border border-stone-200 text-center hover:shadow-md transition-shadow"
                data-testid={`value-${index}`}
              >
                <div className="w-14 h-14 rounded-sm bg-emerald/10 flex items-center justify-center mx-auto mb-4 text-emerald">
                  {value.icon}
                </div>
                <h3 className="text-lg font-serif font-semibold text-stone-900 mb-2">
                  {t(value.titleKey)}
                </h3>
                <p className="text-stone-600 text-sm">
                  {t(value.descKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ecosystem Section */}
      <section className="py-24 md:py-32 bg-emerald text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('about.ecosystemTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4">
              {t('about.ecosystemTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm p-8 rounded-sm"
              data-testid="holder-brand"
            >
              <img 
                src={LOGO_URL} 
                alt="Holder & Co." 
                className="h-10 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-stone-300 mb-6">
                {t('about.holderBrand')}
              </p>
              <ul className="space-y-3 text-sm text-stone-300">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-bronze" />
                  {t('about.holderServices.credit')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-bronze" />
                  {t('about.holderServices.ma')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-bronze" />
                  {t('about.holderServices.consulting')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-bronze" />
                  {t('about.holderServices.fiscal')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-bronze" />
                  {t('about.holderServices.legal')}
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-bronze/20 backdrop-blur-sm p-8 rounded-sm"
              data-testid="has-brand"
            >
              <div className="h-10 w-auto mb-4 overflow-hidden rounded">
                <img 
                  src={HAS_LOGO_URL} 
                  alt="HAS - Hold Account Service" 
                  className="h-10 w-auto object-contain"
                />
              </div>
              <p className="text-stone-300 mb-6">
                {t('about.hasBrand')}
              </p>
              <ul className="space-y-3 text-sm text-stone-300">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  {t('about.hasServices.accounts')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  {t('about.hasServices.escrow')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  {t('about.hasServices.payments')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  {t('about.hasServices.cards')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  {t('about.hasServices.marketplace')}
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section - Quem Somos */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('about.teamTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4">
              {t('about.teamTitle')}
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              {t('about.teamDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.nameKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-sm overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                data-testid={`team-member-${index}`}
              >
                <div className="h-40 bg-gradient-to-br from-emerald to-emerald-light flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center">
                    <span className="text-2xl font-serif font-bold text-white">
                      {t(member.nameKey).split(' ').map(n => n[0]).slice(0, 2).join('')}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h4 className="font-serif font-semibold text-stone-900 mb-1">
                    {t(member.nameKey)}
                  </h4>
                  <p className="text-bronze text-sm font-medium mb-3">
                    {t(member.roleKey)}
                  </p>
                  <p className="text-stone-600 text-sm">
                    {t(member.descKey)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Offices Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="offices-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('about.officesTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4">
              {t('about.officesTitle')}
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              {t('about.officesDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {offices.map((office, index) => (
              <motion.div
                key={office.city}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
                data-testid={`office-${index}`}
              >
                <div className="relative overflow-hidden rounded-sm mb-4">
                  <img 
                    src={office.image}
                    alt={office.city}
                    className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {office.isMatriz && (
                    <span className="absolute top-4 left-4 bg-bronze text-white text-xs font-medium px-3 py-1 rounded-sm">
                      {t('about.offices.matriz')}
                    </span>
                  )}
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-bronze mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-serif font-semibold text-stone-900 mb-1">
                      {office.city}
                    </h3>
                    <p className="text-stone-600 text-sm">{office.address}</p>
                    <p className="text-stone-500 text-sm">{office.cep}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-stone-600 mb-4">
              <Building2 className="inline-block h-5 w-5 mr-2 text-emerald" />
              {t('about.agentsInfo')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-6">
              {t('about.ctaTitle')}
            </h2>
            <p className="text-stone-600 text-lg mb-8">
              {t('about.ctaDesc')}
            </p>
            <Button 
              asChild
              className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
              data-testid="sobre-cta-btn"
            >
              <Link to="/contato" className="flex items-center gap-2">
                {t('about.ctaBtn')}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Sobre;
